import { useEffect, useRef, useState } from "react";

const TICK = 250;
const ACTIVITY_EVENTS = [
  "mousemove",
  "mousedown",
  "keydown",
  "scroll",
  "wheel",
  "touchstart",
];

let leaveAllowedUntil = 0;

export const allowLeave = (duration = Infinity) => {
  leaveAllowedUntil = Date.now() + duration;
};

const toDelay = (value) => {
  const seconds = Number(value);
  return value !== null && value !== "" && seconds > 0 ? seconds : null;
};

const useHelpPrompts = ({
  stepId,
  idleDelay,
  enabled,
  busy,
  onIdle,
  onTabLeave,
}) => {
  const [remaining, setRemaining] = useState(null);
  const shown = useRef({ idle: false, tabLeave: false });
  const state = useRef({});
  state.current = { enabled, busy, onIdle, onTabLeave };
  const delay = toDelay(idleDelay);

  useEffect(() => {
    const onBeforeUnload = (event) => {
      if (Date.now() < leaveAllowedUntil) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, []);

  useEffect(() => {
    shown.current = { idle: false, tabLeave: false };
    let deadline = Date.now() + (delay || 0) * 1000;

    const reset = () => {
      deadline = Date.now() + (delay || 0) * 1000;
    };

    const tick = () => {
      const { enabled, busy, onIdle } = state.current;
      if (!delay || !enabled || shown.current.idle || document.hidden) {
        return setRemaining(null);
      }
      if (busy) {
        reset();
        return setRemaining(null);
      }
      const left = deadline - Date.now();
      if (left <= 0) {
        shown.current.idle = true;
        setRemaining(null);
        return onIdle();
      }
      setRemaining(Math.ceil(left / 1000));
    };

    const onVisibilityChange = () => {
      reset();
      if (!document.hidden) return;
      const { enabled, busy, onTabLeave } = state.current;
      if (!enabled || busy || shown.current.tabLeave) return;
      shown.current.tabLeave = true;
      onTabLeave();
    };

    ACTIVITY_EVENTS.forEach((event) =>
      window.addEventListener(event, reset, { passive: true }),
    );
    document.addEventListener("visibilitychange", onVisibilityChange);
    const interval = setInterval(tick, TICK);
    tick();

    return () => {
      clearInterval(interval);
      ACTIVITY_EVENTS.forEach((event) =>
        window.removeEventListener(event, reset),
      );
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [stepId, delay]);

  return { remaining, total: delay };
};

export default useHelpPrompts;
